import { FEEDBACK_DRAFT_PART, type FeedbackDraftView } from "@esi/shared";
import { useQueryClient } from "@tanstack/react-query";
import { Flag } from "lucide-react";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { useMe } from "../me.ts";
import { feedbackDraftKeys, useFeedbackDrafts, useReportProblem } from "./drafts.ts";
import { FeedbackDraftDialog } from "./FeedbackDialog.tsx";

/** A message as the thread holds it: its ID and parts (a streamed draft is one of them). */
type MessageLike = { id: string; parts: readonly unknown[] };

/**
 * Feedback from a conversation (openspec change `feedback-from-conversation`), for the thread:
 * the drafts under its answers (streamed with the answer, else read again), and "Report a
 * problem" on an answer or a failed turn's notice, which opens the feedback dialog prefilled
 * with the turn's server-built context. Nothing when the tenant turned feedback off.
 */
export function useConversationFeedback(
  conversationId: string | undefined,
  active: boolean,
  /** A turn is running: once it ends, its draft (if any) is read again. */
  busy = false,
  /** The thread's messages: a streamed draft joins the drafts read, so it outlives the part. */
  messages: readonly MessageLike[] = [],
) {
  const me = useMe();
  const enabled = active && conversationId !== undefined && me.data?.feedback === true;
  const drafts = useFeedbackDrafts(conversationId, enabled);
  const queryClient = useQueryClient();
  const streamed = messages
    .map(
      (m) =>
        (
          m.parts.find((p) => (p as { type?: unknown }).type === FEEDBACK_DRAFT_PART) as
            | { data?: FeedbackDraftView }
            | undefined
        )?.data,
    )
    .filter((d): d is FeedbackDraftView => d !== undefined);
  const streamedKey = streamed.map((d) => d.id).join(",");
  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed by the streamed drafts' IDs.
  useEffect(() => {
    if (!enabled || streamed.length === 0 || conversationId === undefined) {
      return;
    }
    queryClient.setQueryData<FeedbackDraftView[]>(
      feedbackDraftKeys.conversation(conversationId),
      (drafts = []) => [...drafts, ...streamed.filter((d) => !drafts.some((x) => x.id === d.id))],
    );
  }, [enabled, conversationId, streamedKey, queryClient]);
  const wasBusy = useRef(busy);
  const { refetch } = drafts;
  useEffect(() => {
    if (wasBusy.current && !busy && enabled) {
      void refetch();
    }
    wasBusy.current = busy;
  }, [busy, enabled, refetch]);
  const report = useReportProblem(conversationId ?? "");
  const [open, setOpen] = useState<FeedbackDraftView | null>(null);
  const opener = useRef<HTMLElement | null>(null);
  // The agent's drafts by answer; "Report a problem" drafts open in the dialog, never as a card.
  const byMessage = useMemo(
    () =>
      new Map(
        (drafts.data ?? [])
          .filter((d) => d.trigger !== "answer_action" && d.messageId !== null)
          .map((d) => [d.messageId as string, d]),
      ),
    [drafts.data],
  );
  return {
    enabled,
    /** The agent's draft under an answer: the stored one once read, else the streamed part. */
    draftFor(message: MessageLike): FeedbackDraftView | null {
      if (!enabled) {
        return null;
      }
      const part = message.parts.find(
        (p) => (p as { type?: unknown }).type === FEEDBACK_DRAFT_PART,
      ) as { data?: FeedbackDraftView } | undefined;
      return byMessage.get(message.id) ?? part?.data ?? null;
    },
    /** "Report a problem": the answer's draft (or a failed turn's), then the dialog. */
    start(input: { messageId?: string; failed?: boolean }, from?: HTMLElement | null) {
      opener.current = from ?? null;
      report.mutate(input, { onSuccess: setOpen });
    },
    pending: report.isPending,
    failed: report.isError,
    dialog: open ? (
      <FeedbackDraftDialog
        draft={open}
        open
        onClose={() => setOpen(null)}
        returnFocus={() => opener.current}
      />
    ) : null,
  };
}

/** The failed turn's notice action: "Report a problem". */
export function ReportFailedTurn(props: {
  onReport: (from: HTMLElement) => void;
  pending: boolean;
}): ReactNode {
  const { t } = useTranslation();
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      disabled={props.pending}
      onClick={(e) => props.onReport(e.currentTarget)}
    >
      <Flag aria-hidden="true" strokeWidth={1.75} />
      {t("feedbackDraft.reportProblem")}
    </Button>
  );
}
